"use client";

import { type SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";

import { toast } from "@/utils/customToast";
import { agreeOnTerms } from "@/features/auth/schemas";
import availableLanguages from "@/i18n/availableLanguages";
import { confirmOnboarding } from "@/features/auth/api/onboarding";
import useAuthStore from "@/features/auth/store/useAuthStore";
import PATHS from "@/routes/paths";
import errorHandler from "@/features/auth/utils/errorHandler";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const confirmationFormSchema = z.object({
  agreeOnTerms,
});

type ConfirmationFormValues = z.infer<typeof confirmationFormSchema>;

interface ConfirmationFormProps {
  goToPrevStep: () => void;
  goToNextStep: () => void;
}

const ConfirmationForm = ({ goToPrevStep, goToNextStep }: ConfirmationFormProps) => {
  const { t } = useTranslation(["forms", "common", "auth"]);
  const navigate = useNavigate();

  const { phoneNumber, language, timezone, secondaryEmail, birthDate, firstName, lastName } =
    useAuthStore((state) => state.onboardingData);
  const accountType = useAuthStore((state) => state.onboardingData.accountType);

  const isStudentUnder16 = (() => {
    if (accountType !== "student" || !birthDate) return false;
    const bd = new Date(birthDate);
    const today = new Date();
    const threshold = new Date(today);

    threshold.setFullYear(today.getFullYear() - 16);

    return bd > threshold;
  })();

  const selectedLang =
    availableLanguages.find((item) => item.language === language) || availableLanguages[0];

  const form = useForm<ConfirmationFormValues>({
    resolver: zodResolver(confirmationFormSchema),
    mode: "onTouched",
    defaultValues: {
      agreeOnTerms: true,
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    control,
  } = form;

  const onSubmit: SubmitHandler<ConfirmationFormValues> = async () => {
    if (isStudentUnder16) {
      goToNextStep();

      return;
    }
    try {
      await confirmOnboarding();

      navigate(PATHS.REDIRECT);
    } catch (error) {
      const apiErrors = errorHandler<"confirmation">(error);

      if (apiErrors?.confirmation) {
        apiErrors.confirmation.forEach((error) => {
          toast.error(error);
        });
      }
    }
  };

  return (
    <Form {...form}>
      <form className="grid w-full gap-12" onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ConfirmationInfoBox
            title={t("auth:signUp.name", "Name")}
            value={`${firstName} ${lastName}`}
          />
          <ConfirmationInfoBox
            title={t("auth:signUp.phoneNumber", "Phone Number")}
            value={phoneNumber || ""}
          />
          <ConfirmationInfoBox
            title={t("auth:signUp.birthDate", "Birth Date")}
            value={birthDate || ""}
          />
          <ConfirmationInfoBox
            title={t("auth:signUp.secondaryEmail", "Secondary Email")}
            value={secondaryEmail || t("auth:signUp.notProvided", "not provided")}
          />
          <ConfirmationInfoBox
            title={t("auth:signUp.timezone", "Timezone")}
            value={
              t(
                //@ts-ignore
                `timezones:${timezone}`,
              ) || ""
            }
          />
          <div
            className={cn(
              "hover:bg-muted/30 h-20 flex-col items-start rounded-2xl bg-linear-to-tr from-purple-500/10 to-indigo-500/10 px-5 py-2 shadow-lg backdrop-blur-sm transition-all duration-200",
            )}
          >
            <p className="text-accent-foreground mb-2 font-medium">
              {t("auth:signUp.communicationLanguage")}
            </p>
            <img
              alt={selectedLang.name}
              className="h-8 w-10 rounded object-cover"
              src={selectedLang.flagImg || "/placeholder.svg"}
            />
          </div>
        </div>
        <div className="grid gap-4">
          <FormField
            control={control}
            name="agreeOnTerms"
            render={({ field: { value, onBlur, onChange } }) => (
              <FormItem className="flex flex-row items-center space-y-0 space-x-3">
                <FormControl>
                  <Checkbox checked={value} onBlur={onBlur} onCheckedChange={onChange} />
                </FormControl>
                <FormLabel>
                  {t("forms:labels.agreeOnTerms")}
                  <span className="text-destructive ml-1">*</span>
                </FormLabel>
              </FormItem>
            )}
          />
          <div className={"flex w-full gap-2"}>
            <Button
              className={
                "bg-muted/20 hover:bg-muted/30 border-border/50 w-20 shadow-lg backdrop-blur-sm"
              }
              disabled={isSubmitting}
              size="lg"
              type="button"
              variant="outline"
              onClick={goToPrevStep}
            >
              <Icon icon={"mdi:arrow-left"} />
              {t("common:back")}
            </Button>
            <Button
              className={
                "bg-accent-foreground/90 hover:bg-accent-foreground text-background w-full shadow-xl"
              }
              disabled={isSubmitting || !form.formState.isValid}
              size="lg"
              type="submit"
            >
              {t("common:signUp")}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
};

function ConfirmationInfoBox({ title, value }: { title: string; value: string }) {
  return (
    <div
      className={
        "hover:bg-muted/30 flex h-20 flex-col justify-center rounded-2xl bg-linear-to-tr from-purple-500/10 to-indigo-500/10 px-5 py-2 shadow-lg backdrop-blur-sm transition-all duration-200"
      }
    >
      <p className="text-accent-foreground mb-2 font-medium">{title}</p>
      <p className="text-muted-foreground">{value}</p>
    </div>
  );
}

export default ConfirmationForm;
